import { useQuery } from '@apollo/client';
import type { Document } from '@contentful/rich-text-types';
import type { ApolloError } from 'apollo-client';

import type { Locale } from 'bundles/shared-articles/types';
import type {
  SeoContentHubPageBottomContentLinksFragment,
  SeoContentHubPageFeaturedContentFieldsFragment,
  SeoContentMainHubPageQuery,
  SeoContentMainHubPageQueryVariables,
} from 'bundles/unified-seo-content-common/__generated__/contentfulTypes';
import { CATEGORY_TYPE_NAME, DEFAULT_LOCALE_CODE } from 'bundles/unified-seo-content-common/constants';
import { seoContentMainHubPageQuery } from 'bundles/unified-seo-content-common/queries/SeoContentHubPageQuery';
import type {
  NavigationLinkWithSubItems,
  SEOContentPageType,
  SeoContentCard,
} from 'bundles/unified-seo-content-common/types';
import { queryErrorHandler } from 'bundles/unified-seo-content-common/utils';
import { getNavigationLinks } from 'bundles/unified-seo-content-common/utils/hubPageUtils';

type InnerProps = {
  pageType: SEOContentPageType;
  preview?: boolean;
  subfolderLocaleCode?: Locale;
};

export type PageDataProps = {
  title?: string | null;
  introduction?: string | null;
  featuredImageUrl?: string | null;
  featuredImageAlt?: string | null;
  featuredContent?: Array<SeoContentCard | null>;
  bottomContentLinks?: SeoContentHubPageBottomContentLinksFragment;
  bottomContentJson?: Document | null;
  featuredContentByCategoryList?: Array<SeoContentHubPageFeaturedContentFieldsFragment | null>;
  metaTitle?: string | null;
  metaDescription?: string | null;
  isHubPageDataLoading: boolean;
  navigationLinks: NavigationLinkWithSubItems[];
  error?: ApolloError;
};

export const useSeoContentMainHubPageQuery = ({
  pageType,
  preview = false,
  subfolderLocaleCode = DEFAULT_LOCALE_CODE,
}: InnerProps): PageDataProps => {
  const { languageCode, countryCode } = subfolderLocaleCode;

  const { data, loading, error } = useQuery<SeoContentMainHubPageQuery, SeoContentMainHubPageQueryVariables>(
    seoContentMainHubPageQuery,
    {
      variables: {
        pageType,
        preview,
        countryCode,
        languageCode,
      },
      context: { clientName: preview ? 'contentfulPreviewGql' : 'contentfulGql' },
      errorPolicy: 'all',
      onError: queryErrorHandler({ extraMessage: 'Error fetching seo content main hub page data' }),
    }
  );

  const hubPageData = data?.hubPageWithCatgoriesData?.items[0];
  const featuredContentByCategoryList = data?.featuredContentByCategoryList?.items?.filter(
    (pageInfo) => pageInfo?.filterBy?.__typename === CATEGORY_TYPE_NAME.primaryCategory
  );
  const {
    title,
    introduction,
    featuredImage,
    featuredContentCollection,
    bottomContent,
    navigation,
    metaTitle,
    metaDescription,
  } = hubPageData || {};
  const { url: featuredImageUrl, description: featuredImageAlt } = featuredImage || {};
  const featuredContent = featuredContentCollection?.items;
  const { json: bottomContentJson, links: bottomContentLinks } = bottomContent || {};

  const navigationLinks = navigation ? getNavigationLinks(navigation, { countryCode, pageType }) : [];

  return {
    title,
    introduction,
    featuredImageUrl,
    featuredImageAlt,
    featuredContent,
    bottomContentJson,
    bottomContentLinks,
    featuredContentByCategoryList,
    metaTitle,
    metaDescription,
    isHubPageDataLoading: loading,
    navigationLinks,
    error,
  };
};
